/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { isEmpty } from 'lodash';

import {
  calculateWorkVolume,
  fetchDataFromS3Bucket,
  getFacilityIcon,
  getStoredCustomerDetails,
  JobDetails,
  NUMBER_OF_ITEMS_PER_PAGE,
  UserRoles,
} from '../../../../Shared/Utilities/utils';

import JobCreateApiService from '../../../Jobs/Services/JobCreateService';
import WorkOrdersApiService from '../../../Jobs/Services/WorkOrdersService';
import FacilityApiService from '../../../Facility/Services/FacilityApiService';
import CustomerApiService from '../../../Customer/Services/CustomerService';
import { JobBidEstimation, JobType, ServiceCategoryV3, UpdateWorkOrderInput, WorkOrderStatus } from '../../../../API';

import { IVendorJobAwardData } from '../../Models/Marketplace.model';

import VendorJobAward from './AwardJobCard';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';

import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';
import { AwardCardContainer, LoaderTag, NoDataContainer, NoDataTag } from './Marketplace.Style';
import { VendorType } from '../../../../Shared/Models/Vendors.Models';
import { getWorkOrderStatus } from '../../Utilities/Jobs.Utils';
import CreateBidServices from '../../Services/CreateBidServices';
import MasterDataApiService from '../../../../Shared/Services/MasterDataService';
import useGlobalSearch from './useGlobalSearch';
import { IMarketplaceTabProps } from '../../Models/constants';

const AwardedJobs = ({ searchText = '' }: IMarketplaceTabProps): JSX.Element => {
  const { t } = useTranslation(['vendor', 'tableHeader', 'altTexts', 'vendorBidCreation', 'jobAward']);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [upcomingJob, setUpcomingJob] = useState<IVendorJobAwardData[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isPrimaryLoading, setIsPrimaryLoading] = useState<boolean>(false);
  const [loadingCardId, setLoadingCardId] = useState<string>('');
  const [viewDetailsLoading, setViewDetailsLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const noDataImageStyles: React.CSSProperties = {
    width: '20rem',
  };

  /* eslint-disable sonarjs/cognitive-complexity */
  const fetchVendorUpcomingJob = async () => {
    const customerDetails = getStoredCustomerDetails();
    const vendorId = customerDetails.customerId;
    const vendorCompanyName = customerDetails.organizationName;
    const authInfo = localStorage.getItem('auth');
    const parsedAuthInfo = authInfo ? JSON.parse(authInfo) : {};
    const { userId = '' } = getStoredCustomerDetails() || {};

    setIsLoading(true);
    try {
      // const jobStartDate = getDateString(new Date());  // Will remove jobStartDate value once QA verify the change.
      let jobCreatorRoleName = '';
      if (!isEmpty(parsedAuthInfo?.roles || [])) {
        if (parsedAuthInfo?.roles?.includes('AccountHolder' as any)) {
          jobCreatorRoleName = UserRoles.AccountHolder;
        } else if (parsedAuthInfo?.roles?.includes('Admin' as any)) {
          jobCreatorRoleName = UserRoles.Admin;
        }
      }

      if (isEmpty(jobCreatorRoleName) && !isEmpty(parsedAuthInfo) && !isEmpty(parsedAuthInfo?.roles)) {
        // If user role name neither AccountHolder nor Admin
        jobCreatorRoleName = parsedAuthInfo?.roles?.[0] || '';
      }

      let upcomingJobResponse = !isEmpty(jobCreatorRoleName)
        ? await WorkOrdersApiService.fetchAllWorkOrders({
            vendorId,
            status: `${WorkOrderStatus.Created},${WorkOrderStatus.Accepted}`,
            jobCreatedBy: userId,
            jobCreatorRoleName,
            searchText: searchTextRef?.current,
          })
        : { data: [], metadata: [], errors: [] };

      if (upcomingJobResponse.metadata.totalCount > NUMBER_OF_ITEMS_PER_PAGE) {
        upcomingJobResponse = await WorkOrdersApiService.fetchAllWorkOrders({
          vendorId,
          status: `${WorkOrderStatus.Created},${WorkOrderStatus.Accepted}`,
          jobCreatedBy: userId,
          jobCreatorRoleName,
          searchText: searchTextRef?.current,
          pageNumber: 1,
          limit: upcomingJobResponse.metadata.totalCount,
        });
      }
      if (!isEmpty(upcomingJobResponse?.data)) {
        const selectedWorkOrder = upcomingJobResponse?.data ?? [];
        if (!isEmpty(selectedWorkOrder)) {
          const upcomingJobsWithFacilityInfo = await Promise.all(
            selectedWorkOrder?.map(async (job: any) => {
              try {
                let facilityId;
                if (job?.facilities) {
                  facilityId = job?.facilities[0]?.facilityId;
                } else {
                  facilityId = '';
                }
                const facilityAddressResponse = await FacilityApiService.facility(
                  facilityId as string,
                  job.customerId as string
                );
                const customerDetailsResponse = await CustomerApiService.getCustomerDetailsResponse(
                  job.customerId as string
                );

                const vendorLogoResponse = await fetchDataFromS3Bucket(
                  navigate,
                  `vendor/${vendorId}/company-logos/${vendorId}`
                );
                const customerLogoResponse = await fetchDataFromS3Bucket(
                  navigate,
                  `customer/${job.customerId}/company-logos/${job.customerId}`
                );
                let facilityIcon = '';
                if (
                  facilityAddressResponse &&
                  facilityAddressResponse?.errors?.length === 0 &&
                  facilityAddressResponse?.data &&
                  facilityAddressResponse?.data?.address?.logoUrl
                ) {
                  facilityIcon = (await getFacilityIcon(
                    facilityAddressResponse?.data?.address?.logoUrl as string
                  )) as string;
                }
                if (customerDetailsResponse.errors.length) {
                  setSnackbarMessage(customerDetailsResponse?.errors[0]?.message as string);
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
                }
                const { legalName } = customerDetailsResponse?.data?.customer ?? '';

                return {
                  vendorIcon: vendorLogoResponse,
                  customerIcon: customerLogoResponse,
                  jobType: job.jobType,
                  customerCompanyName: legalName,
                  customerFacilityName: facilityAddressResponse?.data?.buildingName,
                  vendorCompanyName,
                  vendorFacilityName: vendorCompanyName,
                  jobName: job.name,
                  customerFacilityAddress:
                    (facilityAddressResponse.data?.address?.addressLine2 ||
                      facilityAddressResponse.data?.address?.addressName) ??
                    '',
                  jobStartDate: job?.jobStartDate ?? '',
                  jobEndDate: job?.jobEndDate ?? '',
                  totalAmount: job?.totalAmount,
                  workVolume: calculateWorkVolume(job?.services),
                  workOrderId: job?.workOrderId,
                  workOrderStatus: job?.status,
                  vendorId,
                  customerId: job.customerId,
                  bidId: job?.bidId ?? '',
                  jobId: job?.jobId ?? '',
                  facilityId: facilityId ?? '',
                  logo: facilityIcon,
                };
              } catch (error) {
                console.error('Error in processing job:', job, error);
                throw error;
              }
            })
          );
          setUpcomingJob(upcomingJobsWithFacilityInfo);
        } else {
          setUpcomingJob([]);
        }
      } else if (isEmpty(upcomingJobResponse?.data) && isEmpty(upcomingJobResponse?.errors)) {
        setUpcomingJob([]);
      } else if (upcomingJobResponse?.errors?.length && upcomingJobResponse?.errors[0]?.message) {
        setSnackbarMessage(upcomingJobResponse?.errors[0]?.message as string);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error('Error', e);
    }
  };

  const updateWorkOrderStatus = async (
    payload: UpdateWorkOrderInput,
    name: string,
    customerId = '',
    jobId = '',
    bidId = ''
  ) => {
    setLoadingCardId(payload.workOrderId);
    setIsPrimaryLoading(true);
    try {
      const workOrderResponse = await WorkOrdersApiService.updateWorkOrderStatus(payload);
      if (workOrderResponse?.data && workOrderResponse?.errors?.length === 0) {
        const status = workOrderResponse?.data?.status;
        navigateJobDetailsPage(customerId, jobId, status, bidId);
      } else {
        setSnackbarMessage(workOrderResponse?.errors[0]?.message as string);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (e) {
      console.error('Error', e);
    }
    setIsPrimaryLoading(false);
  };

  const getServiceCategories = async (customerId: string) => {
    try {
      const masterData = await MasterDataApiService.getAllServiceCategoryV3(customerId);
      return masterData.data ?? [];
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const navigateJobDetailsPage = async (customerId: string, jobId: string, status?: string, bidId?: string) => {
    const jobSummaryResponse = await JobCreateApiService.jobSummaryV2(customerId, jobId);
    if (jobSummaryResponse?.data && jobSummaryResponse?.errors?.length === 0) {
      const jobData = jobSummaryResponse?.data;
      const facilityResponse = await FacilityApiService.facility(
        jobData?.jobFacilities?.[0]?.facilityId as string,
        jobData?.customerId as string
      );
      const isOtjJob =
        !isEmpty(jobSummaryResponse?.data?.jobCustodian) &&
        jobSummaryResponse?.data?.jobType !== JobType.Recurring &&
        jobSummaryResponse?.data?.jobType !== JobType.Periodic;
      const executionType =
        isOtjJob &&
        (jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === VendorType.SubVendor ||
        jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === VendorType.All ||
        jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === VendorType.SearchVendors
          ? JobDetails.SubVendor
          : JobDetails.Self);
      const estimationType =
        isOtjJob &&
        (jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === VendorType.All ||
        jobSummaryResponse?.data?.jobVendors?.[0]?.mdVendorType === VendorType.SearchVendors
          ? JobDetails.EstimateWithinPeazy
          : JobDetails.EstimateOutsidePeazy);
      const mBidEstimations = await CreateBidServices.allJobBidEstimation(jobId, bidId ?? '');
      const serviceCategories = await getServiceCategories(customerId);

      const jobServices = mBidEstimations?.data?.map((bidEstimationItem: JobBidEstimation) => {
        const selectedService: any = serviceCategories?.find(
          (serviceCategoryItem: ServiceCategoryV3) =>
            serviceCategoryItem.serviceCategoryId === bidEstimationItem?.mdCategoryId
        );
        return {
          service: selectedService?.serviceCategoryName ?? '',
          id: 0,
          quantity: bidEstimationItem.quantity,
          unit: bidEstimationItem.mdUnits,
          mdServiceId: bidEstimationItem.mdCategoryId,
          numOfHours: bidEstimationItem.quantity,
          ratePerHour: bidEstimationItem.rate,
          totalJobEstimation: bidEstimationItem.totalCost,
          serviceMeasure: bidEstimationItem?.mdMeasureType,
          estimationId: bidEstimationItem?.estimationId,
        };
      });

      navigate(`/onetimejobdetails?id=${jobId}`, {
        state: {
          facilityItem: {
            address: facilityResponse?.data?.address || {},
            jobStatus: jobSummaryResponse?.data?.dataStatus,
            status: getWorkOrderStatus(status as WorkOrderStatus),
            executionType: executionType,
            estimationType: estimationType,
          },
          facility: { ...jobData?.jobFacilities?.[0], address: facilityResponse?.data?.address || {} },
          job: {
            ...jobSummaryResponse?.data,
            address: facilityResponse?.data?.address || {},
            jobStatus: jobSummaryResponse?.data?.dataStatus,
            executionType: executionType,
            estimationType: estimationType,
            bidId: bidId,
            jobServices: jobServices,
          },
        },
      });
    } else {
      setSnackbarMessage(jobSummaryResponse?.errors[0]?.message as string);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleViewDetails = async (
    jobId: string,
    customerId: string,
    workOrderId: string,
    workOrderStatus?: string
  ) => {
    setLoadingCardId(workOrderId);
    setViewDetailsLoading(true);
    await navigateJobDetailsPage(customerId, jobId, workOrderStatus);
    setViewDetailsLoading(false);
  };

  const searchTextRef = useGlobalSearch(searchText, () => {
    fetchVendorUpcomingJob();
  });

  useEffect(() => {
    fetchVendorUpcomingJob();
  }, []);
  return (
    <>
      <AwardCardContainer>
        {isLoading ? (
          <LoaderTag>
            <CircularProgress size={30} sx={{ color: theme.palette.primary.dark }} />
          </LoaderTag>
        ) : (
          <>
            {upcomingJob.length > 0 ? (
              <>
                {upcomingJob.map((job: IVendorJobAwardData) => {
                  return (
                    <VendorJobAward
                      data={job}
                      isPrimaryButtonLoading={isPrimaryLoading}
                      viewDetailsLoading={viewDetailsLoading}
                      loadingCardId={loadingCardId}
                      handleViewDetails={handleViewDetails}
                      handleStart={updateWorkOrderStatus}
                    />
                  );
                })}
              </>
            ) : (
              <NoDataContainer>
                <img src={Icons.ErrorCautionImg} style={{ ...noDataImageStyles }} />
                <NoDataTag>{t('vendor:noAwardedJobsMsg')}</NoDataTag>
              </NoDataContainer>
            )}
          </>
        )}
      </AwardCardContainer>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default AwardedJobs;
